import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase";
import {
  buildPeriodDates,
  formatPeriodLabel,
  getCurrentPeriodStart,
  movePeriod,
  toIsoDate,
} from "./dateUtils";
import "./App.css";

const TEXT_PALETTE = [
  "#000000",
  "#1F1F1F",
  "#7F7F7F",
  "#D9D9D9",
  "#FFFFFF",
  "#C00000",
  "#FF0000",
  "#FFC000",
  "#FFFF00",
  "#92D050",
  "#00B050",
  "#00B0F0",
  "#0070C0",
  "#002060",
  "#7030A0",
  "#9BBB59",
  "#4F81BD",
  "#F79646",
  "#8064A2",
  "#948A54",
];

const BACKGROUND_PALETTE = [
  "#E3F2FD",
  "#E8F5E9",
  "#FFF3E0",
  "#F3E5F5",
  "#E0F7FA",
  "#FCE4EC",
  "#F1F8E9",
  "#EDE7F6",
  "#FFF8E1",
  "#ECEFF1",
];

const ROW_CATEGORIES = [
  { key: "staff", label: "スタッフ" },
  { key: "vehicle", label: "車両" },
  { key: "equipment", label: "備品" },
];

export const KEEP_ALIVE_INTERVAL_MS = 6 * 60 * 60 * 1000;
export const DEFAULT_ZOOM = 0.2;
export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 1.2;
const ZOOM_STORAGE_PREFIX = "zoom-v6";

const cellKey = (rowId, workDate) => `${rowId}|${workDate}`;
const cellConflictKey = (rowId, workDate) =>
  `cell:${cellKey(rowId, workDate)}`;
const rowConflictKey = (rowId) => `row:${rowId}`;

export function calculateFitMonthZoom(viewportWidth, dayCount) {
  const safeViewport = Math.max(320, Number(viewportWidth) || 0);
  const safeDayCount = Math.max(1, Number(dayCount) || 1);
  const usableWidth = Math.max(1, safeViewport - 370);
  const rawZoom = usableWidth / (safeDayCount * 88);
  const steppedZoom = Math.floor(rawZoom / 0.05) * 0.05;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(steppedZoom.toFixed(2))));
}

export function calculateDisplayZoomPercent(zoom) {
  if (zoom < DEFAULT_ZOOM) {
    return Math.round((zoom / DEFAULT_ZOOM - 1) * 100);
  }
  return Math.round((zoom - DEFAULT_ZOOM) * 100);
}

export function calculateFitTableZoom(
  viewportWidth,
  availableHeight,
  dayCount,
  rowCount
) {
  const safeWidth = Math.max(240, Number(viewportWidth) || 0) - 36;
  const safeHeight = Math.max(120, Number(availableHeight) || 0) - 12;
  const baselineWidth = 70 * 2 + Math.max(1, dayCount) * 20 + 50 + 8;
  const baselineHeight =
    34 + Math.max(0, rowCount) * 34 + ROW_CATEGORIES.length * 30 + 8;
  const rawScale = Math.min(
    1,
    safeWidth / baselineWidth,
    safeHeight / baselineHeight
  );
  const scale = rawScale < 1 ? rawScale * 0.82 : 1;
  const steppedZoom = Math.floor((DEFAULT_ZOOM * scale) / 0.01) * 0.01;
  return Math.min(DEFAULT_ZOOM, Math.max(MIN_ZOOM, Number(steppedZoom.toFixed(2))));
}

function calculateZoomedSize(zoom, factor, minimum, baseline) {
  const safeZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
  if (safeZoom >= DEFAULT_ZOOM) {
    return Math.max(baseline, Math.round(factor * safeZoom));
  }
  return Math.max(minimum, Math.round(baseline * (safeZoom / DEFAULT_ZOOM)));
}

export function normalizeCell(record) {
  if (!record) {
    return {
      text: "",
      color: "",
      revision: 0,
      updatedAt: null,
      updatedBy: null,
    };
  }
  return {
    text: record.text ?? "",
    color: record.color ?? record.text_color ?? "",
    revision: Number(record.revision || 0),
    updatedAt: record.updatedAt ?? record.updated_at ?? null,
    updatedBy: record.updatedBy ?? record.updated_by ?? null,
  };
}

export async function keepProjectActive(client = supabase) {
  try {
    const { error } = await client
      .from("schedule_rows")
      .select("id")
      .limit(1);
    return error || null;
  } catch (error) {
    return error;
  }
}

function normalizeRow(record) {
  const cells = {};
  for (const cell of record.schedule_cells || []) {
    cells[cell.work_date] = normalizeCell(cell);
  }
  return {
    id: record.id,
    position: record.position,
    name: record.name || "",
    rowCategory: record.row_category || "staff",
    revision: Number(record.revision || 0),
    updatedAt: record.updated_at || null,
    updatedBy: record.updated_by || null,
    cells,
  };
}

function LoginScreen() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setMessage("");
  };

  const signIn = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (signInError) setError("ログインできません。メールとパスワードを確認してください。");
    setBusy(false);
  };

  const sendPasswordRecovery = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    const { error: recoveryError } = await supabase.auth.resetPasswordForEmail(
      email.trim(),
      { redirectTo: `${window.location.origin}/` }
    );
    setBusy(false);
    if (recoveryError) {
      setError("再設定メールを送信できませんでした。少し待ってからもう一度お試しください。");
      return;
    }
    setMessage(
      "再設定メールを送信しました。届いたメールのリンクを開いて、新しいパスワードを設定してください。"
    );
  };

  if (mode === "recovery") {
    return (
      <main className="loginPage">
        <form className="loginCard" onSubmit={sendPasswordRecovery}>
          <div className="loginMark">勤務表</div>
          <h1>パスワード再設定</h1>
          <p>登録されているメールアドレスへ、再設定用のリンクを送ります。</p>
          <label>
            メールアドレス
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
              required
            />
          </label>
          {error && <div className="formError" role="alert">{error}</div>}
          {message && <div className="formSuccess" role="status">{message}</div>}
          <button className="primaryButton" disabled={busy} type="submit">
            {busy ? "送信中..." : "再設定メールを送る"}
          </button>
          <button
            className="textButton"
            type="button"
            onClick={() => changeMode("login")}
            disabled={busy}
          >
            ログイン画面に戻る
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="loginPage">
      <form className="loginCard" onSubmit={signIn}>
        <div className="loginMark">勤務表</div>
        <h1>日勤簿</h1>
        <p>会社から発行されたアカウントでログインしてください。</p>
        <label>
          メールアドレス
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          パスワード
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        {error && <div className="formError" role="alert">{error}</div>}
        <button className="primaryButton" disabled={busy} type="submit">
          {busy ? "確認中..." : "ログイン"}
        </button>
        <button
          className="textButton"
          type="button"
          onClick={() => changeMode("recovery")}
          disabled={busy}
        >
          パスワードを忘れた場合
        </button>
      </form>
    </main>
  );
}

function PasswordDialog({ onClose, isRecovery = false }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const updatePassword = async (event) => {
    event.preventDefault();
    setMessage("");
    if (password.length < 12) {
      setMessage("パスワードは12文字以上にしてください。");
      return;
    }
    if (password !== confirmation) {
      setMessage("確認用パスワードが一致しません。");
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      setMessage(`変更できません: ${error.message}`);
      return;
    }
    window.alert("パスワードを変更しました。");
    onClose();
  };

  return (
    <div className="modalBackdrop" role="presentation">
      <form className="passwordDialog" onSubmit={updatePassword} role="dialog" aria-modal="true" aria-labelledby="password-title">
        <h2 id="password-title">
          {isRecovery ? "新しいパスワードを設定" : "パスワード変更"}
        </h2>
        <p>12文字以上の、他のサービスで使っていないパスワードを設定してください。</p>
        <label>
          新しいパスワード
          <input
            type="password"
            autoComplete="new-password"
            minLength="12"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoFocus
            required
          />
        </label>
        <label>
          新しいパスワード（確認）
          <input
            type="password"
            autoComplete="new-password"
            minLength="12"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            required
          />
        </label>
        {message && <div className="formError">{message}</div>}
        <div className="dialogActions">
          {!isRecovery && (
            <button type="button" onClick={onClose} disabled={busy}>キャンセル</button>
          )}
          <button className="primaryButton" type="submit" disabled={busy}>
            {busy ? "変更中..." : isRecovery ? "設定する" : "変更する"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function App() {
  const initialPeriod = useMemo(() => toIsoDate(getCurrentPeriodStart()), []);
  const [periodStart, setPeriodStart] = useState(initialPeriod);
  const periodDates = useMemo(() => buildPeriodDates(periodStart), [periodStart]);

  const [authReady, setAuthReady] = useState(false);
  const [session, setSession] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("connecting");
  const [savingKeys, setSavingKeys] = useState([]);
  const [conflicts, setConflicts] = useState({});
  const [presenceByCell, setPresenceByCell] = useState({});
  const [activeCell, setActiveCell] = useState(null);
  const [currentTextColor, setCurrentTextColor] = useState("#000000");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return params.get("type") === "recovery";
  });

  const zoomKey = `${ZOOM_STORAGE_PREFIX}:${periodStart}`;
  const [zoom, setZoom] = useState(() => {
    const saved = Number(
      localStorage.getItem(`${ZOOM_STORAGE_PREFIX}:${initialPeriod}`)
    );
    if (saved) return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, saved));
    return DEFAULT_ZOOM;
  });

  const rowsRef = useRef(rows);
  const channelRef = useRef(null);
  const dirtyCellsRef = useRef(new Set());
  const dirtyRowsRef = useRef(new Set());
  const cellTimersRef = useRef(new Map());
  const rowTimersRef = useRef(new Map());
  const persistCellRef = useRef(null);
  const persistRowRef = useRef(null);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    const saved = Number(localStorage.getItem(zoomKey));
    setZoom(
      saved
        ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, saved))
        : DEFAULT_ZOOM
    );
  }, [periodDates.length, zoomKey]);

  useEffect(() => {
    localStorage.setItem(zoomKey, String(zoom));
  }, [zoom, zoomKey]);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session || null);
      setAuthReady(true);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      setAuthReady(true);
      if (event === "PASSWORD_RECOVERY") setIsPasswordRecovery(true);
      if (!nextSession) setRows([]);
    });
    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) return undefined;

    const timer = window.setInterval(async () => {
      const keepAliveError = await keepProjectActive();
      if (keepAliveError) {
        console.warn("Supabaseの定期接続に失敗しました。", keepAliveError);
      }
    }, KEEP_ALIVE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [session]);

  const mergeRemoteRows = useCallback((remoteRows, localRows) => {
    const localById = new Map(localRows.map((row) => [row.id, row]));
    return remoteRows.map((remoteRow) => {
      const localRow = localById.get(remoteRow.id);
      if (!localRow) return remoteRow;

      const merged = { ...remoteRow, cells: { ...remoteRow.cells } };
      if (dirtyRowsRef.current.has(remoteRow.id)) {
        merged.name = localRow.name;
        merged.revision = localRow.revision;
      }
      for (const [workDate, localCell] of Object.entries(localRow.cells)) {
        if (dirtyCellsRef.current.has(cellKey(remoteRow.id, workDate))) {
          merged.cells[workDate] = localCell;
        }
      }
      return merged;
    });
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (!session) return;
      if (!silent) setLoading(true);
      setErrorMessage("");

      const { error: ensureError } = await supabase.rpc("ensure_schedule_category_rows", {
        p_period_start: periodStart,
        p_staff_count: 5,
        p_vehicle_count: 3,
        p_equipment_count: 3,
      });
      if (ensureError) {
        setErrorMessage(`初期行を準備できません: ${ensureError.message}`);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("schedule_rows")
        .select(
          "id, position, name, row_category, revision, updated_at, updated_by, schedule_cells(work_date, text, text_color, revision, updated_at, updated_by)"
        )
        .eq("period_start", periodStart)
        .order("position", { ascending: true });

      if (error) {
        setErrorMessage(`データを読み込めません: ${error.message}`);
        setLoading(false);
        return;
      }

      const remoteRows = (data || []).map(normalizeRow);
      setRows((previous) => mergeRemoteRows(remoteRows, previous));
      setLoading(false);
    },
    [mergeRemoteRows, periodStart, session]
  );

  useEffect(() => {
    if (!session) return;
    setRows([]);
    setConflicts({});
    setActiveCell(null);
    dirtyCellsRef.current.clear();
    dirtyRowsRef.current.clear();
    loadData();
  }, [loadData, periodStart, session]);

  const updatePresence = useCallback((nextActiveCell) => {
    const channel = channelRef.current;
    if (!channel || !session) return;
    channel.track({
      email: session.user.email || "スタッフ",
      period_start: periodStart,
      active_cell: nextActiveCell
        ? cellKey(nextActiveCell.rowId, nextActiveCell.workDate)
        : null,
      online_at: new Date().toISOString(),
    });
  }, [periodStart, session]);

  useEffect(() => {
    if (!session) return undefined;
    let refreshTimer;
    const channel = supabase.channel(`schedule:${periodStart}`, {
      config: { presence: { key: session.user.id } },
    });
    channelRef.current = channel;

    const refresh = (payload) => {
      if (payload.table === "schedule_rows") {
        const affectedPeriod = payload.new?.period_start || payload.old?.period_start;
        if (affectedPeriod !== periodStart) return;
      }
      if (payload.table === "schedule_cells") {
        const rowId = payload.new?.row_id || payload.old?.row_id;
        if (!rowsRef.current.some((row) => row.id === rowId)) return;
      }
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => loadData({ silent: true }), 180);
    };

    channel
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "schedule_rows" },
        refresh
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "schedule_cells" },
        refresh
      )
      .on("presence", { event: "sync" }, () => {
        const nextPresence = {};
        const state = channel.presenceState();
        for (const [userId, entries] of Object.entries(state)) {
          if (userId === session.user.id) continue;
          for (const entry of entries) {
            if (
              entry.period_start !== periodStart ||
              !entry.active_cell
            ) {
              continue;
            }
            if (!nextPresence[entry.active_cell]) nextPresence[entry.active_cell] = [];
            nextPresence[entry.active_cell].push(entry.email || "他のスタッフ");
          }
        }
        setPresenceByCell(nextPresence);
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          setConnectionStatus("online");
          updatePresence(null);
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          setConnectionStatus("offline");
        } else {
          setConnectionStatus("connecting");
        }
      });

    return () => {
      clearTimeout(refreshTimer);
      if (channelRef.current === channel) channelRef.current = null;
      supabase.removeChannel(channel);
      setPresenceByCell({});
    };
  }, [loadData, periodStart, session, updatePresence]);

  const markSaving = useCallback((key, isSaving) => {
    setSavingKeys((previous) => {
      const next = new Set(previous);
      if (isSaving) next.add(key);
      else next.delete(key);
      return Array.from(next);
    });
  }, []);

  const queueCellSave = useCallback((rowId, workDate, delay = 650) => {
    const key = cellKey(rowId, workDate);
    clearTimeout(cellTimersRef.current.get(key));
    const timer = setTimeout(() => persistCellRef.current?.(rowId, workDate), delay);
    cellTimersRef.current.set(key, timer);
  }, []);

  const queueRowSave = useCallback((rowId, delay = 650) => {
    clearTimeout(rowTimersRef.current.get(rowId));
    const timer = setTimeout(() => persistRowRef.current?.(rowId), delay);
    rowTimersRef.current.set(rowId, timer);
  }, []);

  persistCellRef.current = async (rowId, workDate) => {
    const row = rowsRef.current.find((item) => item.id === rowId);
    if (!row) return;
    const draft = normalizeCell(row.cells[workDate]);
    const key = cellKey(rowId, workDate);
    markSaving(key, true);

    const { data, error } = await supabase.rpc("save_schedule_cell", {
      p_row_id: rowId,
      p_work_date: workDate,
      p_text: draft.text,
      p_text_color: draft.color || null,
      p_expected_revision: draft.revision,
    });

    markSaving(key, false);
    if (error) {
      setErrorMessage(`セルを保存できません: ${error.message}`);
      return;
    }
    if (data?.status === "conflict") {
      setConflicts((previous) => ({
        ...previous,
        [cellConflictKey(rowId, workDate)]: {
          kind: "cell",
          rowId,
          workDate,
          draft,
          remote: data.cell ? normalizeCell(data.cell) : null,
        },
      }));
      return;
    }

    const savedCell = data?.cell ? normalizeCell(data.cell) : null;
    const currentRow = rowsRef.current.find((item) => item.id === rowId);
    const currentCell = normalizeCell(currentRow?.cells[workDate]);
    const hasNewerDraft =
      currentCell.text !== draft.text || currentCell.color !== draft.color;

    setRows((previous) =>
      previous.map((item) => {
        if (item.id !== rowId) return item;
        const cells = { ...item.cells };
        if (hasNewerDraft) {
          cells[workDate] = {
            ...normalizeCell(cells[workDate]),
            revision: savedCell?.revision || 0,
          };
        } else if (savedCell) {
          cells[workDate] = savedCell;
        } else {
          delete cells[workDate];
        }
        return { ...item, cells };
      })
    );

    if (hasNewerDraft) {
      queueCellSave(rowId, workDate, 100);
    } else {
      dirtyCellsRef.current.delete(key);
    }
  };

  persistRowRef.current = async (rowId) => {
    const row = rowsRef.current.find((item) => item.id === rowId);
    if (!row) return;
    const draftName = row.name;
    const key = `row:${rowId}`;
    markSaving(key, true);
    const { data, error } = await supabase.rpc("save_schedule_row", {
      p_row_id: rowId,
      p_name: draftName,
      p_expected_revision: row.revision,
    });
    markSaving(key, false);

    if (error) {
      setErrorMessage(`名称を保存できません: ${error.message}`);
      return;
    }
    if (data?.status === "conflict") {
      setConflicts((previous) => ({
        ...previous,
        [rowConflictKey(rowId)]: {
          kind: "row",
          rowId,
          draftName,
          remote: data.row,
        },
      }));
      return;
    }

    const saved = data.row;
    const current = rowsRef.current.find((item) => item.id === rowId);
    const hasNewerDraft = current?.name !== draftName;
    setRows((previous) =>
      previous.map((item) =>
        item.id === rowId
          ? {
              ...item,
              name: hasNewerDraft ? item.name : saved.name,
              revision: Number(saved.revision || 0),
              updatedAt: saved.updated_at,
              updatedBy: saved.updated_by,
            }
          : item
      )
    );
    if (hasNewerDraft) queueRowSave(rowId, 100);
    else dirtyRowsRef.current.delete(rowId);
  };

  useEffect(
    () => () => {
      for (const timer of cellTimersRef.current.values()) clearTimeout(timer);
      for (const timer of rowTimersRef.current.values()) clearTimeout(timer);
    },
    []
  );

  const handleNameChange = (rowId, value) => {
    setRows((previous) =>
      previous.map((row) => (row.id === rowId ? { ...row, name: value } : row))
    );
    dirtyRowsRef.current.add(rowId);
    queueRowSave(rowId);
  };

  const handleCellChange = (rowId, workDate, value) => {
    setRows((previous) =>
      previous.map((row) => {
        if (row.id !== rowId) return row;
        const current = normalizeCell(row.cells[workDate]);
        return {
          ...row,
          cells: {
            ...row.cells,
            [workDate]: {
              ...current,
              text: value,
              color:
                current.color || (value.trim() ? currentTextColor : current.color),
            },
          },
        };
      })
    );
    dirtyCellsRef.current.add(cellKey(rowId, workDate));
    queueCellSave(rowId, workDate);
  };

  const applyPaletteColor = (color) => {
    setCurrentTextColor(color);
    if (!activeCell) return;
    const { rowId, workDate } = activeCell;
    let shouldSave = false;
    setRows((previous) =>
      previous.map((row) => {
        if (row.id !== rowId) return row;
        const current = normalizeCell(row.cells[workDate]);
        shouldSave = Boolean(current.text.trim());
        return {
          ...row,
          cells: {
            ...row.cells,
            [workDate]: { ...current, color },
          },
        };
      })
    );
    if (shouldSave) {
      dirtyCellsRef.current.add(cellKey(rowId, workDate));
      queueCellSave(rowId, workDate);
    }
  };

  const focusCell = (rowId, workDate) => {
    const next = { rowId, workDate };
    setActiveCell(next);
    updatePresence(next);
  };

  const blurCell = () => {
    setActiveCell(null);
    updatePresence(null);
  };

  const addRow = async (category = "staff") => {
    setErrorMessage("");
    const { error } = await supabase.rpc("create_schedule_category_row", {
      p_period_start: periodStart,
      p_row_category: category,
    });
    if (error) setErrorMessage(`行を追加できません: ${error.message}`);
    else loadData({ silent: true });
  };

  const clearRow = async (row) => {
    const label = row.name.trim() || `${row.position}行目`;
    if (!window.confirm(`「${label}」の行を削除しますか？`)) return;
    const { error } = await supabase.rpc("delete_schedule_row", { p_row_id: row.id });
    if (error) setErrorMessage(`行を削除できません: ${error.message}`);
    else loadData({ silent: true });
  };

  const acceptRemote = (key, conflict) => {
    if (conflict.kind === "cell") {
      const localKey = cellKey(conflict.rowId, conflict.workDate);
      dirtyCellsRef.current.delete(localKey);
      clearTimeout(cellTimersRef.current.get(localKey));
      setRows((previous) =>
        previous.map((row) => {
          if (row.id !== conflict.rowId) return row;
          const cells = { ...row.cells };
          if (conflict.remote) cells[conflict.workDate] = conflict.remote;
          else delete cells[conflict.workDate];
          return { ...row, cells };
        })
      );
    } else {
      dirtyRowsRef.current.delete(conflict.rowId);
      clearTimeout(rowTimersRef.current.get(conflict.rowId));
      setRows((previous) =>
        previous.map((row) =>
          row.id === conflict.rowId
            ? {
              ...row,
              name: conflict.remote.name,
              revision: Number(conflict.remote.revision || 0),
              }
            : row
        )
      );
    }
    setConflicts((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const keepLocal = (key, conflict) => {
    if (conflict.kind === "cell") {
      setRows((previous) =>
        previous.map((row) => {
          if (row.id !== conflict.rowId) return row;
          const current = normalizeCell(row.cells[conflict.workDate]);
          return {
            ...row,
            cells: {
              ...row.cells,
              [conflict.workDate]: {
                ...current,
                revision: conflict.remote?.revision || 0,
              },
            },
          };
        })
      );
      dirtyCellsRef.current.add(cellKey(conflict.rowId, conflict.workDate));
      setTimeout(() => queueCellSave(conflict.rowId, conflict.workDate, 0), 0);
    } else {
      setRows((previous) =>
        previous.map((row) =>
          row.id === conflict.rowId
            ? { ...row, revision: Number(conflict.remote.revision || 0) }
            : row
        )
      );
      dirtyRowsRef.current.add(conflict.rowId);
      setTimeout(() => queueRowSave(conflict.rowId, 0), 0);
    }
    setConflicts((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const changePeriod = (offset) => {
    if (savingKeys.length || dirtyCellsRef.current.size || dirtyRowsRef.current.size) {
      setErrorMessage("保存が終わってから期間を移動してください。");
      return;
    }
    setPeriodStart(toIsoDate(movePeriod(periodStart, offset)));
  };

  const backgroundMap = useMemo(() => {
    const values = new Set();
    rows.forEach((row) =>
      Object.values(row.cells).forEach((cell) => {
        const value = cell.text.trim();
        if (value) values.add(value);
      })
    );
    const map = {};
    Array.from(values).forEach((value, index) => {
      map[value] = BACKGROUND_PALETTE[index % BACKGROUND_PALETTE.length];
    });
    return map;
  }, [rows]);

  const rowsByCategory = useMemo(() => {
    const grouped = Object.fromEntries(
      ROW_CATEGORIES.map((category) => [category.key, []])
    );
    rows.forEach((row) => {
      const category = grouped[row.rowCategory] ? row.rowCategory : "staff";
      grouped[category].push(row);
    });
    return grouped;
  }, [rows]);

  const tableStyle = useMemo(
    () => ({
      "--name-width": `${calculateZoomedSize(zoom, 170, 14, 70)}px`,
      "--day-width": `${calculateZoomedSize(zoom, 88, 3, 20)}px`,
      "--cell-height": `${calculateZoomedSize(zoom, 72, 6, 34)}px`,
      "--cell-font": `${calculateZoomedSize(zoom, 14, 4, 8)}px`,
      "--action-width": `${calculateZoomedSize(zoom, 74, 12, 50)}px`,
      "--header-height": `${calculateZoomedSize(zoom, 50, 7, 34)}px`,
      "--header-font": `${calculateZoomedSize(zoom, 11, 4, 7)}px`,
      "--header-day-font": `${calculateZoomedSize(zoom, 17, 4, 10)}px`,
      "--month-font": `${calculateZoomedSize(zoom, 9, 4, 7)}px`,
      "--name-padding": `${calculateZoomedSize(zoom, 0, 0, 8)}px`,
      "--category-height": `${calculateZoomedSize(zoom, 0, 8, 30)}px`,
      "--category-font": `${calculateZoomedSize(zoom, 0, 4, 11)}px`,
      "--category-padding-y": `${calculateZoomedSize(zoom, 0, 0, 3)}px`,
      "--category-padding-x": `${calculateZoomedSize(zoom, 0, 1, 6)}px`,
      "--category-button-font": `${calculateZoomedSize(zoom, 0, 4, 10)}px`,
      "--category-button-padding-y": `${calculateZoomedSize(zoom, 0, 0, 3)}px`,
      "--category-button-padding-x": `${calculateZoomedSize(zoom, 0, 1, 8)}px`,
      "--action-button-padding": `${calculateZoomedSize(zoom, 0, 0, 5)}px`,
      "--action-button-font": `${calculateZoomedSize(zoom, 0, 4, 8)}px`,
      "--action-button-margin": `${calculateZoomedSize(zoom, 0, 0, 2)}px`,
      "--mirror-padding": `${calculateZoomedSize(zoom, 0, 0, 3)}px`,
      "--name-header-font": `${calculateZoomedSize(zoom, 0, 4, 13)}px`,
    }),
    [zoom]
  );

  const fitMonthToScreen = () => {
    setZoom(calculateFitMonthZoom(window.innerWidth, periodDates.length));
  };

  const fitWholeTableToScreen = () => {
    const tableTop = tableWrapRef.current?.getBoundingClientRect().top || 260;
    const availableHeight = window.innerHeight - tableTop - 14;
    setZoom(
      calculateFitTableZoom(
        window.innerWidth,
        availableHeight,
        periodDates.length,
        rows.length
      )
    );
  };

  if (!authReady) return <div className="centerMessage">認証を確認中...</div>;
  if (!session) return <LoginScreen />;

  const conflictEntries = Object.entries(conflicts);
  const zoomPct = calculateDisplayZoomPercent(zoom);
  const zoomLabel = `${zoomPct > 0 ? "+" : ""}${zoomPct}%`;
  const statusLabel =
    connectionStatus === "online"
      ? "リアルタイム接続中"
      : connectionStatus === "offline"
        ? "接続が切れています"
        : "接続中...";

  return (
    <main className="appPage">
      <header className="topbar">
        <div>
          <div className="eyebrow">SHIFT MANAGEMENT</div>
          <h1>日勤簿</h1>
        </div>
        <div className="accountBox">
          <span>{session.user.email}</span>
          <button type="button" onClick={() => setShowPasswordDialog(true)}>
            パスワード変更
          </button>
          <button type="button" onClick={() => supabase.auth.signOut()}>
            ログアウト
          </button>
        </div>
      </header>

      <section className="periodBar">
        <button type="button" onClick={() => changePeriod(-1)} aria-label="前の期間">
          ← 前
        </button>
        <div>
          <div className="periodLabel">{formatPeriodLabel(periodStart)}</div>
          <div className={`connection ${connectionStatus}`}>{statusLabel}</div>
        </div>
        <button type="button" onClick={() => changePeriod(1)} aria-label="次の期間">
          次 →
        </button>
      </section>

      <section className="toolbar">
        <button
          className="primaryButton compact"
          type="button"
          onClick={() => addRow("staff")}
        >
          ＋ スタッフ行
        </button>
        <div className="autosaveStatus">
          {savingKeys.length ? `${savingKeys.length}件を保存中...` : "変更は自動保存されます"}
        </div>
        <button type="button" onClick={fitMonthToScreen}>
          1か月表示
        </button>
        <button type="button" onClick={fitWholeTableToScreen}>
          表全体表示
        </button>
        <label className="zoomControl">
          表示倍率 {zoomLabel}
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
        </label>
      </section>

      <section className="palettePanel" aria-label="文字色">
        <div className="paletteTitle">文字色</div>
        <div className="swatches">
          {TEXT_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              className={`swatch ${currentTextColor === color ? "selected" : ""}`}
              style={{ backgroundColor: color }}
              title={color}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => applyPaletteColor(color)}
            />
          ))}
          <input
            className="customColor"
            type="color"
            value={currentTextColor}
            onChange={(event) => applyPaletteColor(event.target.value)}
            title="任意の色"
          />
        </div>
      </section>

      {errorMessage && (
        <div className="notice errorNotice">
          <span>{errorMessage}</span>
          <button type="button" onClick={() => setErrorMessage("")}>閉じる</button>
        </div>
      )}

      {conflictEntries.map(([key, conflict]) => (
        <div className="notice conflictNotice" key={key}>
          <span>
            他のスタッフも同じ{conflict.kind === "cell" ? "セル" : "名称"}を変更しました。
          </span>
          <div>
            <button type="button" onClick={() => acceptRemote(key, conflict)}>
              相手の変更を採用
            </button>
            <button className="primaryButton compact" type="button" onClick={() => keepLocal(key, conflict)}>
              自分の変更で上書き
            </button>
          </div>
        </div>
      ))}

      {loading ? (
        <div className="centerMessage">シフトを読み込み中...</div>
      ) : (
        <div className="tableWrap" style={tableStyle} ref={tableWrapRef}>
          <table>
            <thead>
              <tr>
                <th className="stickyName nameHeader">名称</th>
                {periodDates.map((date) => (
                  <th className={date.isWeekend ? "weekend" : ""} key={date.iso}>
                    <span className="dateMonth">{date.month}月</span>
                    <strong>{date.day}</strong>
                    <span>({date.weekday})</span>
                  </th>
                ))}
                <th className="mirrorNameHeader">名称</th>
                <th className="actionHeader">操作</th>
              </tr>
            </thead>
            <tbody>
              {ROW_CATEGORIES.map((category) => (
                <Fragment key={category.key}>
                  <tr className={`categoryRow ${category.key}`}>
                    <th className="stickyName categoryLabel" scope="rowgroup">
                      {category.label}
                    </th>
                    <td colSpan={periodDates.length + 2}>
                      <button type="button" onClick={() => addRow(category.key)}>
                        ＋ {category.label}行を追加
                      </button>
                    </td>
                  </tr>
                  {rowsByCategory[category.key].map((row) => (
                    <tr key={row.id}>
                      <td className="stickyName nameCell">
                        <input
                          value={row.name}
                          onChange={(event) =>
                            handleNameChange(row.id, event.target.value)
                          }
                          placeholder={`${category.label}名`}
                          aria-label={`${category.label}${row.position}行目の名称`}
                        />
                      </td>
                      {periodDates.map((date) => {
                        const cell = normalizeCell(row.cells[date.iso]);
                        const key = cellKey(row.id, date.iso);
                        const editors = presenceByCell[key] || [];
                        const isActive =
                          activeCell?.rowId === row.id &&
                          activeCell?.workDate === date.iso;
                        return (
                          <td
                            className={`shiftCell ${
                              date.isWeekend ? "weekendCell" : ""
                            } ${editors.length ? "beingEdited" : ""} ${
                              isActive ? "active" : ""
                            }`}
                            key={date.iso}
                            style={{
                              backgroundColor:
                                backgroundMap[cell.text.trim()] || "#fff",
                            }}
                          >
                            {editors.length > 0 && (
                              <div className="editorBadge" title={editors.join("、")}>
                                {editors[0]} が編集中
                              </div>
                            )}
                            <textarea
                              value={cell.text}
                              onChange={(event) =>
                                handleCellChange(row.id, date.iso, event.target.value)
                              }
                              onFocus={() => focusCell(row.id, date.iso)}
                              onBlur={blurCell}
                              style={{
                                color: cell.color || "#000000",
                                textShadow:
                                  (cell.color || "").toUpperCase() === "#FFFFFF"
                                    ? "0 0 2px rgba(0,0,0,.75)"
                                    : "none",
                              }}
                              aria-label={`${
                                row.name || `${category.label}${row.position}行目`
                              } ${date.month}月${date.day}日`}
                            />
                          </td>
                        );
                      })}
                      <td className="mirrorNameCell" title={row.name}>
                        {row.name || "—"}
                      </td>
                      <td className="actionCell">
                        <button type="button" onClick={() => clearRow(row)}>
                          行削除
                        </button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(showPasswordDialog || isPasswordRecovery) && (
        <PasswordDialog
          isRecovery={isPasswordRecovery}
          onClose={() => {
            setShowPasswordDialog(false);
            setIsPasswordRecovery(false);
            window.history.replaceState({}, document.title, window.location.pathname);
          }}
        />
      )}
    </main>
  );
}
